/**
 * CardLeadUnified - Composant unifié pour LEADS et PROJETS
 * 
 * @prop {string} variant - "lead" | "project"
 * @prop {object} lead - Données du lead/projet
 * @prop {object} context - Contexte ManageLeads
 * @prop {function} altT - Traduction alternative
 * @prop {array} templatesWidgetList - Templates disponibles
 * @prop {object} customStatusConfig - Configuration des statuts (pour utilisation autre variant lead/project)
 * @prop {array} statusOrder - Ordre des statuts
 * @prop {function} saveCb - Callback après sauvegarde (pour utilisation autre lead/project)
 * 
 * VARIANT "lead" :
 * - Structure plate (1 niveau)
 * - Layout : Header → Pipeline → [Actions | Files]
 * - Fichiers depuis lead.leadFiles
 * 
 * VARIANT "project" :
 * - Structure hiérarchique (Projet → Personnes → Versions)
 * - Layout : Header → [Sidebar | Main avec tabs versions]
 * - Fichiers depuis currentVersion.leadFiles
 */

import React from 'react';
import moment from 'moment';
import { Badge, Button, Alert } from "reactstrap";
import WidgetBreadcrumb from 'components/WidgetBreadcrumb';
import FilesCustom from 'components/FilesCustom';
import * as commonFunc from 'scripts/common';
import Swal from "sweetalert2";

// Import des fonctions existantes
import { saveLead, showDialogLead, dialogAltLead, finalizeProject, sendEmailDialog, sendRecapEmailDialog } from '../views/pages/ManageLeads';

function CardLead(props) {
    const {
        variant = "lead", // "lead" | "project"
        lead,
        context,
        altT,
        templatesWidgetList,
        customStatusConfig: externalStatusConfig,
        statusOrder,
        hasPrices,
        shouldVisit,
        recall,
        saveCb: externalSaveCb
    } = props;

    const filesCustomRef = React.useRef(null);

    const isProject = variant === "project";

    const actSvcIDProject = 801;


    const personSelectedVersion = (person) => {
        var idVersion;
        var num = 0;
        var hasAct = false;
        person.versions?.forEach((version) => {
            if (hasAct) {
                if (version.serviceID === actSvcIDProject && version.recall > num) {
                    num = version.recall;
                    idVersion = version.idLead;
                }
            }
            else {
                if (version.serviceID === actSvcIDProject) {
                    hasAct = true;
                    num = version.recall;
                    idVersion = version.idLead;
                }
                else if (version.recall > num) {
                    num = version.recall;
                    idVersion = version.idLead;
                }
            }
        });
        return idVersion;
    };

    // ========== STATE ==========
    const [collapsed, setCollapsed] = React.useState(true);


    React.useEffect(() => {
        if (props.expandAll === true) setCollapsed(false);
        if (props.expandAll === false) setCollapsed(true);
    }, [props.expandStamp]);

    // State spécifique au mode project
    const [selectedPersonID, setSelectedPersonID] = React.useState(
        isProject && lead.persons?.length ? lead.persons[0].idLead : null
    );
    const [selectedVersionID, setSelectedVersionID] = React.useState(() => {
        if (isProject) {
            if (lead.persons?.length) {
                return personSelectedVersion(lead.persons[0]);
            }
        }
        return null;
    });

    // ========== LOGIQUE LEAD (extras, services) - Avant customStatusConfig ==========
    let serviceClass = "";
    let statusClass = "";

    /*if (!isProject) {
        statusClass = `status-${lead.statusID}`;
    }*/

    // ========== CONFIGURATION STATUS avec Tooltips ==========
    const customStatusConfig = externalStatusConfig || (() => {
        const config = {};
        context.state.status?.forEach(status => {
            // Filtrage selon hasPrices et shouldVisit pour variant lead
            if (!isProject) {
                if (!hasPrices && status.idStatus === 3003) return;
                if (!shouldVisit && (status.idStatus === 3004 || status.idStatus === 3007)) return;
            }

            config[status.idStatus] = status;
            const statusObj = status;
            if (!statusObj.bgColor && statusObj.css) {
                switch (true) {
                    case statusObj.css.indexOf("purple-300") >= 0: statusObj.bgColor = "#BA68c8"; break;
                    case statusObj.css.indexOf("amber-600") >= 0: statusObj.bgColor = "#ffb300"; break;
                    case statusObj.css.indexOf("cyan-600") >= 0: statusObj.bgColor = "#00acc1"; break;
                    case statusObj.css.indexOf("green-400") >= 0: statusObj.bgColor = "#66bb6a"; break;
                    case statusObj.css.indexOf("bg-danger") >= 0: statusObj.bgColor = "#DC3545"; break;
                    case statusObj.css.indexOf("bg-success") >= 0: statusObj.bgColor = "#198754"; break;
                    case statusObj.css.indexOf("bg-primary") >= 0: statusObj.bgColor = "#0D6EFD"; break;
                    case statusObj.css.indexOf("bg-secondary") >= 0: statusObj.bgColor = "#6C757D"; break;
                    case statusObj.css.indexOf("bg-info") >= 0: statusObj.bgColor = "#0DCAF0"; break;
                    case statusObj.css.indexOf("bg-warning") >= 0: statusObj.bgColor = "#FFC107"; break;
                    default: break;
                }
            }
            if (!statusObj.textColor) statusObj.textColor = statusObj.bgColor;
            let classFa = "";
            if (statusObj.css?.indexOf("fa-")) {
                statusObj.css.split(" ").forEach((classElem) => {
                    classFa += (classElem.startsWith("fa-") ? (classElem + " ") : "");
                });
                if (classFa) statusObj.icon = "fa " + classFa;
            }
        });

        // ========== TOOLTIPS STATUS 3004 (Visite) et 3006 (Perdu) ==========
        if (lead.extras) {
            if (!isProject) {
                // Tooltip Visite planifiée
                if (config[3004]) {
                    const visitExtras = lead.extras.map(ext => {
                        if (ext.name === "visiteDate") return commonFunc.formatDateTime(commonFunc.restStringToDate(ext.value), true);
                        if (ext.name === "visitItv") return ext.value;
                        return null;
                    }).filter(ext => ext !== null);
                    if (visitExtras.length) {
                        config["3004tooltip"] = { title: "Visite", value: visitExtras };
                    }
                }

                // Tooltip Prospect perdu
                if (config[3006]) {
                    const lossExtras = lead.extras.filter(ext => ext.name === "lossReason");
                    if (lossExtras.length) {
                        config["3006tooltip"] = { title: "Perdu", value: lossExtras[0].value };
                    }
                }
            }
            else {
                // Tooltip Accepté
                if (config[3005]) {
                    const closeExtras = lead.extras.filter(ext => ext.name === "closeDetails");
                    if (closeExtras.length) {
                        config["3005tooltip"] = { title: "Accepté", value: closeExtras[0].value };
                    }
                }
                if (config[3006]) {
                    const closeExtras = lead.extras.filter(ext => ext.name === "closeDetails");
                    if (closeExtras.length) {
                        config["3006tooltip"] = { title: "Rejeté", value: closeExtras[0].value };
                    }
                }
            }
        }

        return config;
    })();

    // ========== LOGIQUE PROJECT (personnes/versions) ==========
    let currPerson = null;
    let currentVersion = null;

    if (isProject) {
        const currPersonFiltered = lead.persons?.filter(person => person.idLead === selectedPersonID);
        if (currPersonFiltered?.length) currPerson = currPersonFiltered[0];

        const currentVersionFiltered = currPerson?.versions?.filter(version => version.idLead === selectedVersionID);
        if (currentVersionFiltered?.length) currentVersion = currentVersionFiltered[0];
    }

    // Source des données selon le variant
    const dataSource = isProject ? currentVersion : lead;
    const statusHistory = dataSource?.flows;

    // ========== REFRESH LEAD (pour project) ==========
    const refreshLead = (targetLead) => {
        const args = { id: targetLead.idLead };
        if (context.state.altMode) args.level = 3;

        Swal.fire({
            title: context.props.i18nManager.t("gal_loading"),
            didOpen: () => { Swal.showLoading() },
            allowOutsideClick: false,
            allowEscapeKey: false,
            allowEnterKey: false
        });

        commonFunc.getMethod("../api/lead/GetLead", args).then((response) => {
            const dataNew = response.data;
            if (dataNew.isSuccess) {

                //Ajustement comme GetList
                dataNew.result.parent = null;
                dataNew.result.billingAddress = null;

                Swal.close();

                const idLocalLead = parseInt(targetLead.idLead);
                let flag = -1;
                let breakAll = false;
                const leads = context.state.leads;

                leads.forEach((localLead, index) => {
                    if (flag < 0 && !breakAll) {
                        flag = (localLead.idLead === idLocalLead) ? index : -1;
                        if (flag < 0 && localLead.persons) {
                            localLead.persons?.forEach((person, indexP) => {
                                if (flag < 0 && !breakAll) {
                                    if (person.versions) {
                                        person.versions?.forEach((version, indexV) => {
                                            if (flag < 0) {
                                                flag = (version.idLead === idLocalLead) ? indexV : -1;
                                            }
                                        });
                                        if (flag >= 0) {
                                            person.versions[flag] = dataNew.result;
                                            person.client = dataNew.result.client;
                                            person.groupID = dataNew.result.groupID;
                                            person.group = dataNew.result.group;
                                            breakAll = true;
                                        } else if (person.parentID === targetLead.parentID && person.clientID === targetLead.clientID) {
                                            person.versions.push(dataNew.result);
                                            if (person.recall < dataNew.result.recall) person.recall = dataNew.result.recall;
                                            person.client = dataNew.result.client;
                                            person.groupID = dataNew.result.groupID;
                                            person.group = dataNew.result.group;
                                            breakAll = true;
                                            setSelectedVersionID(dataNew.result.idLead);
                                        }
                                    }
                                    if (!breakAll) {
                                        flag = (person.idLead === idLocalLead) ? indexP : -1;
                                    }
                                }
                            });
                            if (!breakAll) {
                                if (flag >= 0) {
                                    const versionsList = localLead.persons[flag].versions;
                                    localLead.persons[flag] = dataNew.result;
                                    localLead.persons[flag].versions = versionsList;
                                    breakAll = true;
                                } else if (localLead.idLead === targetLead.parentID) {
                                    const newVersion = Object.assign({}, dataNew.result);
                                    localLead.persons.push(dataNew.result);
                                    if (!dataNew.result.versions) dataNew.result.versions = [];
                                    dataNew.result.versions.push(newVersion);
                                    if (dataNew.result.recall < newVersion.recall) dataNew.result.recall = newVersion.recall;
                                    breakAll = true;
                                    setSelectedPersonID(dataNew.result.idLead);
                                    setSelectedVersionID(newVersion.idLead);
                                }
                            }
                        } else if (localLead.idLead === targetLead.parentID) {
                            const newVersionP = Object.assign({}, dataNew.result);
                            if (!localLead.persons) localLead.persons = [];
                            localLead.persons.push(dataNew.result);
                            if (!dataNew.result.versions) dataNew.result.versions = [];
                            dataNew.result.versions.push(newVersionP);
                            if (dataNew.result.recall < newVersionP.recall) dataNew.result.recall = newVersionP.recall;
                            breakAll = true;
                            setSelectedPersonID(dataNew.result.idLead);
                            setSelectedVersionID(newVersionP.idLead);
                        }
                    }
                });

                if (flag >= 0 && !breakAll) {
                    dataNew.result.cRecommandations = leads[flag].cRecommandations;//!isProject

                    const personsList = leads[flag].persons;
                    leads[flag] = dataNew.result;
                    leads[flag].persons = personsList;
                }
                context.setState({ leads: leads, loading: false, dataStamp: new Date().getTime(), isShowDetails: false });
            } else {
                Swal.fire({
                    icon: "error",
                    title: context.props.i18nManager.t("gal_error"),
                    html: context.props.i18nManager.t("lead.failLoadLead") + " :<br>" + dataNew.errorMessage
                });
            }
        });
    };

    const removeVersion = (version, project) => {
        var prevVersion;
        var personContainer = project.persons?.filter(person => person.clientID === version.clientID);
        if (personContainer?.length && personContainer[0].versions?.length) {
            prevVersion = personContainer[0].versions[personContainer[0].versions.length - 2];
        }
        context.removeLead(version.idLead, version.title, "lead.rmvVersionTitle").then((data) => {
            if (data.isSuccess && data.result) {
                if (prevVersion) {
                    context.updateLeadStatus(3004, -1, null, prevVersion.idLead, true, true).then(() => {
                        if (version.recall === 1 && project.serviceID === 801) {
                            project.serviceID = 800;
                            project.cNoSwalMode = true;
                            saveLead(project, {}, true, context.props.i18nManager, () => {
                                Swal.fire({
                                    icon: "success",
                                    title: context.props.i18nManager.t("gal_success"),
                                    html: "Retour en compromis avec succès",
                                    didOpen: () => { Swal.hideLoading() },
                                    allowOutsideClick: false,
                                    allowEscapeKey: false,
                                    allowEnterKey: false,
                                }).then((result) => {
                                    //if (callback && typeof (callback) === "function") { callback(); }
                                    context.getLeads(3000, true, () => { });
                                });
                            });
                        }
                        else {
                            Swal.fire({
                                icon: "success",
                                title: context.props.i18nManager.t("gal_success"),
                                html: "Suppression de version effectué",
                                didOpen: () => { Swal.hideLoading() },
                                allowOutsideClick: false,
                                allowEscapeKey: false,
                                allowEnterKey: false,
                            }).then(() => {
                                //context.getLeads(3000, true, () => { });
                                refreshLead(prevVersion);
                            });
                        }
                    });
                }
            }
        });
    };

    const afterNewVersion = (targetLead, currentVersion, cb) => {
        if (targetLead.isSuccess) {
            context.updateLeadStatus(3007, -1, null, currentVersion.idLead, true, true).then(() => {
                Swal.fire({
                    icon: "success",
                    title: context.props.i18nManager.t("gal_success"),
                    html: "Changement de version effectué",
                    didOpen: () => { Swal.hideLoading() },
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                    allowEnterKey: false,
                }).then(() => {
                    refreshLead(currentVersion);
                    cb(targetLead);
                });
            });
        }
        else cb(targetLead);
    };

    const saveCb = externalSaveCb || ((targetLead) => {
        //if (isProject) {
            if (!targetLead.idLead && targetLead.result?.idLead) targetLead = targetLead.result;
            refreshLead(targetLead);
        /*} else {
            context.getLeads(3000, true, () => { });
        }*/
    });

    // ========== CONFIGURATION FICHIERS ==========
    const fileSource = isProject ? currentVersion : lead;

    // Fichiers non-RCP
    const filesAltObj = {
        ...(context.filesType ? { filesType: context.filesType } : { altFilesTypes: "../api/lead/GetLeadFilesType" }),
        files: fileSource?.leadFiles?.map(file => file.file.categ !== "RCP" ? file.file : null).filter(Boolean) || [],
        setFiles: (cptFiles) => { saveCb(fileSource) }
    };

    // Garder uniquement le DERNIER fichier non-RCP
    if (!isProject && filesAltObj.files?.length) {
        var sortedFiles = filesAltObj.files.sort((a, b) => new Date(b.dSysMod).getTime() - new Date(a.dSysMod).getTime());
        var tarifFiles = sortedFiles.filter(file => { return file.categ === "PDF"; });
        filesAltObj.files = [tarifFiles[0], ...sortedFiles.filter(file => { return file.categ !== "PDF"; })];
    }

    // Fichiers RCP
    const filesRcpObj = {
        ...(context.filesType ? { filesType: context.filesType } : { altFilesTypes: "../api/lead/GetLeadFilesType" }),
        files: fileSource?.leadFiles?.map(file => file.file.categ === "RCP" ? file.file : null).filter(Boolean) || []
    };

    // Garder uniquement le DERNIER fichier RCP
    if (filesRcpObj.files?.length) {
        filesRcpObj.files = [filesRcpObj.files.sort((a, b) => new Date(b.dSysMod).getTime() - new Date(a.dSysMod).getTime())[0]];
    }

    // ========== VARIABLES COMMUNES ==========
    const notifDate = lead.dSysNotif ? commonFunc.restStringToDate(lead.dSysNotif) : null;
    const hasNotif = lead.dSysNotif !== lead.dSysCreat && commonFunc.isValidDate(notifDate) && notifDate.getFullYear() > 1970;

    const recallDateExtra = lead.extras?.filter(extra => extra.name === "recallDate");
    const recallHourExtra = lead.extras?.filter(extra => extra.name === "recallHour");
    const nextNotif = recallDateExtra?.length && moment().diff(moment(lead.dSysMod).utc(true), "seconds") > 0 && !commonFunc.isBlank(recallDateExtra[0].value) && new Date(recallDateExtra[0].value).getFullYear() > 1970;
    const recallComplete = (nextNotif && recallHourExtra?.length ? (recallDateExtra[0].value.substring(0, 11) + recallHourExtra[0].value.substring(11, recallHourExtra[0].value.length))
        : (nextNotif ? recallDateExtra[0].value : null));

    // Variables projet
    const spSvcColor = (isProject ? currPerson?.group?.startColor : lead.service?.startColor) || "#6C757D";
    const svcForeColor = (isProject ? currPerson?.service?.foreColor : lead.service?.foreColor) || "#000000";
    //const nbProprio = lead.persons?.filter(person => person.client?.categoryID === 1);

    const projectActsLimited = lead.serviceID === actSvcIDProject;
    const projectLocked = lead.status.step >= 4;//lead.statusID === 3005;

    // Status pour affichage
    const displayStatus = lead;
    const displayStatusID = lead?.statusID;

    // ✅ FIX: Condition pour désactiver les boutons Email (comme l'original)
    const isEmailDisabled = lead.statusID >= 3004;

    // ========== RENDER ==========
    const cardClass = isProject
        ? `projet-card card-${lead.idLead} ${collapsed ? "collapsed" : ""}`
        : `lead-card lead-${lead.idLead}${statusClass} ${lead.priority > 0 ? "priority" : ""} ${collapsed ? "collapsed" : ""}`;


    var supStyle = {};
    var leftColor = customStatusConfig[lead.statusID]?.bgColor || "#6C757D";
    if (leftColor) supStyle.borderLeft = "4px solid " + leftColor;

    return (
        <div className={cardClass} style={{ ...supStyle }}>

            {/* ===== HEADER ===== */}
            <div className={isProject ? "projet-header" : "lead-card-header"} onClick={() => setCollapsed(!collapsed)}>

                {/* Toggle Icon */}
                <span className="toggle-icon">
                    <i className={`fa fa-chevron-${collapsed ? "right" : "down"}`}></i>
                </span>

                {/* === LEAD ONLY: Priority Indicator === */}
                {!isProject && lead.priority > 0 && (
                    <div className="priority-indicator">
                        <span className="priority-dot"></span>
                        <span className="priority-label">Prioritaire</span>
                    </div>
                )}

                {/* === PROJECT ONLY: Badge & Location === */}
                {isProject && (
                    <>
                        <div>
                        <span className="projet-badge">
                            {lead.refExtID ? (lead.extRef ? `${lead.refExtID} • ${lead.extRef}` : lead.refExtID) : lead.extRef}
                        </span>
                        {lead.address?.city && (
                            <span className="projet-location">
                                <i className="fa fa-map-marker-alt"></i>
                                {lead.address.city}
                            </span>
                            )}
                        </div>
                    </>
                )}

                {/* Info principale */}
                <div className={isProject ? "projet-info" : "lead-info"}>
                    {isProject ? (
                        <>
                            <div className="projet-title">{lead.title}</div>
                            <div className="projet-date">
                                Date Projet : {moment(lead.startDate).format("DD MMM YYYY")}
                                {hasNotif && (
                                    <Badge className="ms-2" color="warning">
                                        <i className="fa fa-clock me-1"></i>
                                        Rappel le {commonFunc.formatDateTime(moment(notifDate).utc(true).toDate())}
                                    </Badge>
                                )}
                            </div>
                        </>
                    ) : (
                            <>
                                <div className="lead-title-row">
                                    <div className="lead-title">
                                        {lead.client?.name.toUpperCase()} {commonFunc.capitalize(lead.client?.firstName)}
                                    </div>
                                    {lead.service?.name && (
                                        <span className="lead-service-badge" style={{ backgroundColor: spSvcColor, color: svcForeColor }}>
                                        {lead.service.name}
                                    </span>
                                    )}

                                    {nextNotif && recallComplete && (
                                        <Badge className="ms-2" color="warning">
                                            <i className="fa fa-clock me-1"></i>
                                            A rappeler le {commonFunc.formatDateTime(moment(recallComplete).toDate())}
                                        </Badge>
                                    )}

                                    {lead.recall > 0 && (<span className="badge badge-primary lead-recall">Relance n° {lead.recall}{lead.stepID > 0 ? (" - " + lead.subStatus.name) : ""}</span>)}
                                </div>
                                <div className="lead-contact">
                                    <span className="lead-name hidden">{lead.title}</span>
                                        {lead.client?.tel && (
                                            <span className="lead-phone d-none d-md-block">
                                                <i className="fa fa-phone me-1"></i>
                                                {lead.client.tel}
                                            </span>
                                        )}
                                    {lead.cRecommandation ? <span className="ms-2 text-muted"><i className={`fa ${altT("i18n:lead.recoIcon" + lead.cRecommandation.recommendedAction)}`}></i><i>{lead.cRecommandation.reason}</i></span> : ""}
                                </div>
                            </>
                    )}
                </div>

                {(isProject && displayStatus?.status?.step >= 4) && (
                    <div className="header-status">
                        <span className={`header-status-icon ${/*displayStatus.status?.step >= 4 ? "completed" : "current"*/""}`} style={{ backgroundColor: leftColor }}>
                            <i className={customStatusConfig[displayStatusID]?.icon || "fa fa-circle 2x"} style={{ color: "white" }} ></i>
                        </span>
                        <span className="header-status-label">
                            {customStatusConfig[displayStatusID]?.name || "En cours"}
                        </span>
                    </div>
                )}

                {/* Meta (droite) */}
                <div className={isProject ? "projet-meta" : "lead-meta"}>
                    {isProject ? (
                        <>
                            <div className="projet-owners">
                                {lead.persons?.sort((a, b) => (a.groupID + "" + a.idLead).localeCompare(b.groupID + "" + b.idLead)).map(person => (
                                    <span key={"avatar" + person.idLead} className={`owner-avatar ${person.groupID <= 2 ? "blue" : "green"}`}>
                                        {(person.client.firstName.charAt(0) + person.client.name.charAt(0)).toUpperCase()}
                                    </span>
                                ))}
                            </div>
                            <span className="projet-last-activity">
                                {lead.persons?.length ? `${lead.persons.length} partie(s) • ` : ""}
                                Modifié le {moment(lead.dSysMod).format("DD/MM/YYYY")}
                            </span>
                        </>
                    ) : (
                        <>
                            <span className="lead-association">{lead.extSite?.name}</span>
                            <span className="lead-date">
                                Modifié le {moment(lead.dSysMod).format("DD/MM/YYYY")}
                            </span>
                        </>
                    )}
                </div>

                {/* === PROJECT ONLY: Quick Actions in Header === */}
                {isProject && (
                    <div className="projet-actions-quick" onClick={(e) => e.stopPropagation()}>
                        {context.props.checkRights("LEAD", "U") && (
                            <button className={`action-icon-btn edit ${projectLocked ? "disabled" : ""}`} disabled={projectLocked} onClick={() => showDialogLead(context.props.i18nManager.t("lead.edit"), "UPDPROJECT", lead, context.leadModels, saveLead, saveCb, context.modalGenManager, context.props, context.props.i18nManager)}>
                                <i className="fa fa-edit"></i>
                            </button>
                        )}
                        {context.props.checkRights("LEAD", "D") && (
                            <button className={`action-icon-btn delete ${projectLocked ? " disabled" : ""}`} disabled={projectLocked} onClick={() => context.removeLead(lead.idLead, lead.title)}>
                                <i className="fa fa-trash-alt"></i>
                            </button>
                        )}
                    </div>
                )}

                {/* === LEAD ONLY: Header Priority (collapsed) === */}
                {!isProject && lead.priority > 0 && (
                    <div className="header-priority">
                        <span className="header-priority-dot"></span>
                        <span className="header-priority-label">Prioritaire</span>
                    </div>
                )}

                {/* Header Status (collapsed) - commun */}
                {(!isProject && collapsed && displayStatus) && (
                    <div className="header-status">
                        <span className={`header-status-icon ${displayStatus.status?.step >= 4 ? "completed" : "current"}`} style={{ backgroundColor: leftColor }}>
                            <i className={customStatusConfig[displayStatusID]?.icon || "fa fa-circle 2x"} style={{ color: "white" }} ></i>
                        </span>
                        <span className="header-status-label">
                            {customStatusConfig[displayStatusID]?.name || "En cours"}
                        </span>
                    </div>
                )}
            </div>

            {/* ===== CONTENU ===== */}
            <div className={isProject ? "projet-card-content" : "lead-card-content"}>
                {/* ===== PROJECT: Layout avec Sidebar ===== */}
                {isProject ? (
                    <>
                        {/* Sidebar Sociétés */}
                        <div className="projet-sidebar">
                            <div className="sidebar-title">Parties</div>
                            <div className="societe-list">
                                {(() => {
                                    let lastGroupID;
                                    return lead.persons?.sort((a, b) => (a.groupID + "" + a.idLead).localeCompare(b.groupID + "" + b.idLead)).map(person => {
                                        var personSuppStyle = {};
                                        var svcColor = person.service?.startColor || "#6C757D";
                                        if (svcColor) personSuppStyle.borderLeft = "3px solid " + svcColor;

                                        var feedback = "";
                                        var extrasFeedback = person.extras?.filter(extra => extra.name === "feedback");//TODO : voir pour recup la dernière version plutot pour person
                                        if (extrasFeedback?.length) feedback = extrasFeedback[0].value;
                                        switch (feedback) {
                                            case "good":
                                                personSuppStyle.backgroundColor = "#28a74522";
                                                break;
                                            case "middle":
                                                //personSuppStyle.backgroundColor = "primary";
                                                break;
                                            case "bad":
                                                personSuppStyle.backgroundColor = "#dc354522";
                                                break;
                                            default:
                                                break;
                                        }

                                        const supGroup = lastGroupID !== person.groupID ? (
                                            <div key={"group" + person.groupID} className="sidebar-title" style={{ marginTop: lastGroupID ? "16px" : "0" }}>
                                                {person.group.title.toUpperCase()}
                                            </div>
                                        ) : null;
                                        lastGroupID = person.groupID;

                                        let score = 0;
                                        const extrasScore = person.extras?.filter(extra => extra.name === "score");
                                        if (extrasScore?.length) score = parseFloat(extrasScore[0].value);

                                        return (
                                            <React.Fragment key={"person" + person.idLead}>
                                                {supGroup}
                                                <div
                                                    className={`societe-item ${selectedPersonID === person.idLead ? "active" : ""}`} style={{ ...personSuppStyle }}
                                                    onClick={() => { setSelectedPersonID(person.idLead); setSelectedVersionID(personSelectedVersion(person)); }}
                                                >
                                                    <div className="societe-avatar">
                                                        {(person.client.firstName.charAt(0) + person.client.name.charAt(0)).toUpperCase()}
                                                    </div>
                                                    <div className="societe-info">
                                                        <div className="societe-name">{person.client.firstName} {person.client.name}</div>
                                                        <div className="societe-meta">{person.group.title} • V{person.recall}</div>
                                                    </div>
                                                    {score > 0 && (
                                                        <Badge color={score >= 75 ? "success" : score >= 50 ? "primary" : score >= 25 ? "warning" : "danger"} pill>
                                                            {score}
                                                        </Badge>
                                                    )}
                                                </div>
                                            </React.Fragment>
                                        );
                                    });
                                })()}
                            </div>
                            {!projectLocked && context.props.checkRights("LEAD", "C") && <div className="add-partie-btn" onClick={() => showDialogLead(context.props.i18nManager.t("lead.newPerson"), "NEWPERSON", Object.assign({}, lead), context.leadModels, saveLead, saveCb, context.modalGenManager, context.props, context.props.i18nManager, true)}>
                                <i className="fa fa-plus"></i>
                                <span>Ajouter une partie</span>
                            </div>}
                        </div>

                        {/* Main Content (Project) */}
                        {currPerson?.client && (
                            <div className="projet-main">
                                {/* Header Partie (bleu) */}
                                <div className="partie-card">
                                    <div className="card-header blue">
                                        <div className="partie-header-left">
                                            <div className="partie-avatar">
                                                {(currPerson.client.firstName.charAt(0) + currPerson.client.name.charAt(0)).toUpperCase()}
                                            </div>
                                            <div className="partie-info">
                                                <div className="partie-name">{currPerson.client.firstName} {currPerson.client.name}</div>
                                                <div className="partie-detail">{currPerson.client.email} • {currPerson.client.tel}</div>
                                            </div>
                                        </div>

                                        <div className="partie-versions">
                                            {currPerson.versions?.sort((a, b) => {
                                                if (a.serviceID !== b.serviceID) {
                                                    return a.serviceID - b.serviceID;
                                                }
                                                return parseInt(a.recall) - parseInt(b.recall);
                                            }).map(version => (
                                                <button
                                                    key={`vtab${version.idLead}`}
                                                    className={`version-tab  ${currentVersion.serviceID !== version.serviceID ? "service-old" : ""}  version-svc${version.serviceID} ${selectedVersionID === version.idLead ? "active" : ""}`}
                                                    onClick={() => setSelectedVersionID(version.idLead)}
                                                >
                                                    {version.category?.name}
                                                </button>
                                            ))}
                                            {context.props.checkRights("LEAD", "C") && <button
                                                className="version-tab add"
                                                onClick={() => {
                                                    currentVersion.cNoSwalMode = true;
                                                    showDialogLead(context.props.i18nManager.t("lead.newVersion"), "NEWVERSION", currentVersion, context.leadModels, saveLead, (targetLead) => {
                                                        afterNewVersion(targetLead, currentVersion, saveCb);
                                                    }, context.modalGenManager, context.props, context.props.i18nManager, true)
                                                }}
                                            >
                                                <i className="fa fa-plus"></i>
                                            </button>}
                                        </div>

                                        <div className="partie-actions">
                                            <Badge className={`${currPerson.groupID <= 2 ? "blue" : "green"}`} style={{ backgroundColor: spSvcColor }}>{currPerson?.group?.title}</Badge>
                                            <Badge className="d-none" style={{ backgroundColor: spSvcColor }}>{"TODO currPerson.client?.category?.title"}</Badge>
                                            {context.props.checkRights("LEAD", "S") && (
                                                <>
                                                    <button className="header-btn" onClick={() => sendEmailDialog(currPerson, context.props.dataCfg, context.props.i18nManager)}>
                                                        <i className="fa fa-envelope"></i>
                                                    </button>
                                                    <a href={`tel:${currPerson.client.tel}`} className="header-btn">
                                                        <i className="fa fa-phone"></i>
                                                    </a>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="partie-section">
                                {/* Version Content */}
                                {currPerson.versions?.length && selectedVersionID ? (
                                    currPerson.versions.filter(version => version.idLead === selectedVersionID).map(version => {
                                        const locked = projectLocked || (projectActsLimited && version.serviceID !== actSvcIDProject) || currPerson.recall !== version.recall;
                                        const flowArchiveFiltered = version.flows?.filter(flow => (flow.statutID === 3097 || flow.statutID === 3007)) || [];
                                        const flowArchive = flowArchiveFiltered.length ? flowArchiveFiltered[0] : null;

                                        const versionSvcColor = version?.service?.startColor || "#6C757D";

                                        const versionDetails = version.extras?.filter(extra => extra.name === "versionDetails");
                                        const sessionId = version.extras?.filter(extra => extra.name === "sessionId");

                                        return (
                                            <React.Fragment key={`vc-${version.idLead}`}>
                                                <span className="badge w-auto partie-service" style={{ backgroundColor: versionSvcColor }}>{version.service?.name}</span>
                                                {flowArchive && (version.statutID === 3097 || version.statutID === 3007) && (
                                                    <Alert className="mx-0 mt-3" color="secondary">
                                                        <i className="fa fa-archive me-2"></i>
                                                        Archivé le {moment(flowArchive.dSysMod).format("DD MMM YYYY")} - {flowArchive.name}
                                                    </Alert>
                                                )}

                                                {/* Pipeline */}
                                                <div className="pipeline-section">
                                                    <div className="section-label">Avancement</div>
                                                    <WidgetBreadcrumb
                                                        srcTag={version.idLead}
                                                        srcData={version}
                                                        currentStatus={version.statusID}
                                                        recall={recall}
                                                        statusHistory={version.flows}
                                                        statusOrder={statusOrder}
                                                        statusConfig={customStatusConfig}
                                                        showDates={true}
                                                        showTooltips={true}
                                                        variant="horizontal"
                                                        size="sm"
                                                        altCreateStatus={[3001]}
                                                        lastStatus={[3097, 3007, 3005, 3006]}
                                                    />
                                                </div>

                                                {/* Files Section (Project) */}
                                                {context.props.checkRights("LEAD_FILES", "L") && (
                                                    <div className="files-section">
                                                        <div className="files-grid-2col">
                                                            {/* Documents Joints */}
                                                            <div className="files-column">
                                                                <div className="section-header">
                                                                    <div className="header-title">
                                                                        <i className="fa fa-paperclip"></i>
                                                                        <span>Fichiers</span>
                                                                        <span className="header-count">{filesAltObj.files?.length || 0}</span>
                                                                    </div>
                                                                    {!locked && context.props.checkRights("LEAD_FILES", "C") && (
                                                                        <Button className="m-0" size="sm" color="primary" onClick={() => { filesCustomRef.current.displayAddDialog(); }}>
                                                                            <i className="fa fa-plus me-1"></i>Ajouter
                                                                        </Button>
                                                                    )}
                                                                </div>
                                                                <div className="files-column-body">
                                                                    <div className={`${filesAltObj.files?.length > 0 ? "" : "d-none"}`}>
                                                                        <FilesCustom
                                                                            ref={filesCustomRef}
                                                                            className={"lead-files" + version.idLead}
                                                                            bindID={version.idLead}
                                                                            rightType={"LEAD_FILES"}
                                                                            checkRights={context.props.checkRights}
                                                                            fileTypeID="LEAD"
                                                                            src={version}
                                                                            srcName={version.title}
                                                                            withFilters
                                                                            noUploadBtn
                                                                            shouldRender
                                                                            altRendering
                                                                            noRemove={locked}
                                                                            noContextMenu={locked}
                                                                            defaultFileType={"PRM"} excludedFilesTypes={["DVS", "RCP"]}
                                                                            {...filesAltObj}
                                                                            altFilesUrl="../api/lead/GetLeadFiles"
                                                                            altRemoveFilesUrl="../api/lead/RemoveLeadFile"
                                                                            altUploadFilesUrl="../api/lead/UploadLeadFiles"
                                                                        />
                                                                    </div>
                                                                    {filesAltObj.files?.length <= 0 && (
                                                                        <div className="empty-files">
                                                                            <i className="fa fa-inbox"></i>
                                                                            <span>Aucun document</span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {/* Documents Générés */}
                                                            <div className="files-column">
                                                                <div className="section-header">
                                                                    <div className="header-title">
                                                                        <i className="fa fa-file-export"></i>
                                                                        <span>Récaptitulatif</span>
                                                                        <span className="header-count">{filesRcpObj.files?.length || 0}</span>
                                                                    </div>
                                                                </div>
                                                                <div className="files-column-body">
                                                                    {filesRcpObj.files?.length > 0 ? (
                                                                        <FilesCustom
                                                                            className={"lead-rcp-files" + version.idLead}
                                                                            bindID={version.idLead}
                                                                            rightType={"LEAD_FILES"}
                                                                            checkRights={context.props.checkRights}
                                                                            fileTypeID="LEAD"
                                                                            src={version}
                                                                            srcName={version.title}
                                                                            withFilters
                                                                            noUploadBtn
                                                                            shouldRender
                                                                            altRendering
                                                                            noRemove
                                                                            noContextMenu
                                                                            onlyDelete
                                                                            defaultFileType={"RCP"}
                                                                            {...filesRcpObj}
                                                                            altFilesUrl="../api/lead/GetLeadFiles"
                                                                            altRemoveFilesUrl="../api/lead/RemoveLeadFile"
                                                                            altUploadFilesUrl="../api/lead/UploadLeadFiles"
                                                                        />
                                                                    ) : (
                                                                        <div className="empty-files">
                                                                            <i className="fa fa-inbox"></i>
                                                                            <span>Aucun document généré</span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                <div className="partie-details">
                                                    <div className="w-auto">Details</div>
                                                    <div className="w-auto">{versionDetails?.length ? versionDetails[0].value : ""}</div>
                                                </div>
                                                {/* Actions Bar (Project) */}
                                                <div className="actions-bar">
                                                    <div className="actions-left">
                                                        {context.props.checkRights("LEAD", "R") && (
                                                            <button className="action-btn" onClick={() => refreshLead(version)}>
                                                                <i className="fa fa-sync"></i>
                                                                <span>Actualiser</span>
                                                            </button>
                                                        )}
                                                        {context.props.checkRights("LEAD", "U") && (
                                                            <button className="action-btn" disabled={locked} onClick={() => showDialogLead(context.props.i18nManager.t("lead.updVersion"), "NEWPERSON", version, context.leadModels, saveLead, saveCb, context.modalGenManager, context.props, context.props.i18nManager)}>
                                                                <i className="fa fa-edit"></i>
                                                                <span>Modifier</span>
                                                            </button>
                                                        )}
                                                        {context.props.checkRights("LEAD", "S") && (
                                                            <button className="action-btn primary" disabled={locked} onClick={() => context.generateLeadRecap(version, "CHATGPT5MINI")}>
                                                                <i className="fa fa-play"></i>
                                                                <span>Lancer</span>
                                                            </button>
                                                        )}
                                                        {context.props.checkRights("LEAD", "S") && (
                                                            <button className="action-btn" disabled={!(sessionId?.length > 0)} onClick={() => showDialogLead(context.props.i18nManager.t("lead.askPrompt"), "PROMPTING", version, context.leadModels, saveLead, saveCb, context.modalGenManager, context.props, context.props.i18nManager)}>
                                                                <i className="fa fa-question-circle"></i>
                                                                <span>Interroger</span>
                                                            </button>
                                                        )}
                                                        {context.props.checkRights("LEAD_FLOWS", "L") && (
                                                            <button className="action-btn" onClick={() => context.showFlows(version.idLead, version)}>
                                                                <i className="fa fa-history"></i>
                                                                <span>Historique</span>
                                                            </button>
                                                        )}
                                                    </div>
                                                    <div className="actions-right">
                                                        {!locked && context.props.checkRights("LEAD", "D") && (
                                                            <button className="action-btn danger" onClick={() => removeVersion(version, lead)}>
                                                                <i className="fa fa-trash-alt"></i>
                                                                <span>Supprimer</span>
                                                            </button>
                                                        )}
                                                        {!locked && context.props.checkRights("LEAD", "U") && (
                                                            <button className="action-btn warning" onClick={() => {
                                                                version.cNoSwalMode = true;
                                                                showDialogLead(context.props.i18nManager.t("lead.newVersion"), "NEWVERSION", version, context.leadModels, saveLead, (targetLead) => {
                                                                    afterNewVersion(targetLead, version, saveCb);
                                                                }, context.modalGenManager, context.props, context.props.i18nManager, true)
                                                            }}>
                                                                <i className="fa fa-forward"></i>
                                                                <span>Changer version</span>
                                                            </button>
                                                        )}
                                                        {!locked && !(projectActsLimited || version.serviceID === actSvcIDProject) && context.props.checkRights("LEAD", "U") && (
                                                            <button className="action-btn success" onClick={() => finalizeProject(context, lead, version, saveCb)}>
                                                                <i className="fa fa-clipboard-check"></i>
                                                                <span>Passer en acte</span>
                                                            </button>
                                                        )}
                                                        {!locked && (projectActsLimited || version.serviceID === actSvcIDProject) && context.props.checkRights("LEAD", "U") && (
                                                            <button className="action-btn success" onClick={() => finalizeProject(context, lead, version, saveCb)}>
                                                                <i className="far fa-rectangle-xmark"></i>
                                                                <span>Clôturer</span>
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </React.Fragment>
                                        );
                                    })
                                    ) : null}

                                </div>
                            </div>
                        )}
                    </>
                ) : (
                    /* ===== LEAD: Layout simple ===== */
                    <>
                        {/* Pipeline */}
                        <div className="pipeline-section">
                            <WidgetBreadcrumb
                                srcTag={lead.idLead}
                                srcData={lead}
                                currentStatus={lead.statusID}
                                recall={recall}
                                statusHistory={statusHistory}
                                statusOrder={statusOrder}
                                statusConfig={customStatusConfig}
                                showDates={true}
                                showTooltips={true}
                                variant="horizontal"
                                size="md"
                                altCreateStatus={[3001, 3002]}
                                lastStatus={[3005, 3006]}
                            />
                        </div>

                        {/* Body - Actions + Fichiers */}
                        <div className="lead-card-body">

                            {/* Actions Section */}
                            {context.props.checkRights("LEAD", "S") && (
                                <div className="actions-section">
                                    <div className="actions-title">
                                        <i className="fa fa-bolt"></i>
                                        Prochaines actions
                                    </div>

                                    <div className="action-buttons">
                                        {hasPrices && lead.status?.step <= 2 && (
                                            <button className="btn-action primary" onClick={() => context.manageShopOrder(lead, context)}>
                                                <i className="fa fa-euro-sign"></i>
                                                Envoyer les tarifs
                                            </button>
                                        )}
                                        {shouldVisit && (lead.status?.step <= 2 || lead.statusID === 3003) && (
                                            <button className="btn-action primary" onClick={() => dialogAltLead(context.props.i18nManager.t("lead.scheduleVisit"), "SCHEDULEVISIT", lead, context.leadModels, saveLead, saveCb, context.modalGenManager, context.props, context.props.i18nManager)}>
                                                <i className="fa fa-calendar"></i>
                                                Planifier visite
                                            </button>
                                        )}
                                        {shouldVisit && lead.statusID === 3004 && (
                                            <button className="btn-action primary" onClick={() => context.updateLeadStatus(3007, -1, "Visite réalisée", lead.idLead)}>
                                                <i className="fa fa-map-marker-alt"></i>
                                                Visite réalisée
                                            </button>
                                        )}
                                        {lead.status?.step === 3 && lead.statusID !== 3004 && (
                                            <button className="btn-action warning" onClick={() => context.updateLeadStatus(3008, -1, "Liste d'attente", lead.idLead)}>
                                                <i className="fa fa-pause"></i>
                                                Liste d'attente
                                            </button>
                                        )}
                                        {(lead.statusID === 3003 || lead.statusID === 3007 || lead.statusID === 3008) && (
                                            <button className="btn-action primary" onClick={() => context.updateLeadStatus(3005, -1, "Intervention Planifiée", lead.idLead)}>
                                                <i className="fa fa-calendar-check"></i>
                                                Intervention Planifiée
                                            </button>
                                        )}
                                        {lead.statusID === 3009 && (
                                            <button className="btn-action primary" onClick={() => context.updateLeadStatus(3005, -1, "Prospect transformé", lead.idLead)}>
                                                <i className="fa fa-check"></i>
                                                Transformé Prospect
                                            </button>
                                        )}
                                        {lead.status?.step < 5 && (
                                            <button className="btn-action danger" onClick={() => dialogAltLead(context.props.i18nManager.t("lead.leadloss"), "LEADLOSS", lead, context.leadModels, saveLead, saveCb, context.modalGenManager, context.props, context.props.i18nManager)}>
                                                <i className="fa fa-ban"></i>
                                                Prospect Perdu
                                            </button>
                                        )}
                                        {/* Générer récap - déplacé ici */}
                                        <button className="btn-action recap" onClick={() => context.generateLeadRecap(lead)}>
                                            <i className="fa fa-file-pdf"></i>
                                            Générer récap.
                                        </button>
                                        {/* Nouvelle relance - déplacé ici */}
                                        {(lead.status?.step <= 2 || lead.statusID === 3003) && (
                                            <button className="btn-action recap" onClick={() => context.newRecall(lead)}>
                                                <i className="fa fa-arrow-rotate-right"></i>
                                                Nouvelle relance
                                            </button>
                                        )}
                                    </div>

                                    {/* Quick Actions */}
                                    <div className="quick-actions">
                                        {context.props.checkRights("LEAD", "A") && lead.client?.tel && (
                                            <a href={`callto:${lead.client.tel}`} className="quick-btn">
                                                <i className="fa fa-phone"></i>
                                                Appeler
                                            </a>
                                        )}
                                        {/* ✅ FIX: Email désactivé au lieu de masqué (comme l'original) */}
                                        {context.props.checkRights("LEAD", "A") && (
                                            <button className="quick-btn" disabled={isEmailDisabled} onClick={() => sendEmailDialog(lead, context.props.dataCfg, context.props.i18nManager)}>
                                                <i className="fa fa-envelope"></i>
                                                Email
                                            </button>
                                        )}
                                        {/* ✅ FIX: Email Récap désactivé au lieu de masqué (comme l'original) */}
                                        {context.props.checkRights("LEAD", "A") && (
                                            <button className="quick-btn" disabled={isEmailDisabled} onClick={() => sendRecapEmailDialog(lead, context.props.dataCfg, context.props.i18nManager)}>
                                                <i className="fa fa-envelope"></i>
                                                Email Récap.
                                            </button>
                                        )}
                                        {context.props.checkRights("LEAD", "U") && (lead.status?.step < 4 && lead.statusID !== 3004 && lead.statusID !== 3007) && (
                                            <button className="quick-btn highlight" onClick={() => showDialogLead(altT(templatesWidgetList[0]?.name), templatesWidgetList[0]?.tag, lead, context.leadModels, saveLead, saveCb, context.modalGenManager, context.props, context.props.i18nManager)}>
                                                <i className="fa fa-edit"></i>
                                                Modifier
                                            </button>
                                        )}
                                        {context.props.checkRights("LEAD", "CU") && lead.status?.step < 4 && (
                                                <button className="quick-btn" onClick={() => showDialogLead(altT(templatesWidgetList[0]?.name?.replace("edit", "duplicate")), templatesWidgetList[0]?.tag, lead, context.leadModels, saveLead, () => { context.getLeads(3000, true, () => { }); }/*saveCb*/, context.modalGenManager, context.props, context.props.i18nManager, true)}>
                                                <i className="fa fa-copy"></i>
                                                Dupliquer
                                            </button>
                                        )}
                                        {context.props.checkRights("LEAD", "R") && (
                                            <button className="quick-btn" onClick={() => dialogAltLead(context.props.i18nManager.t("lead.tabDetails"), "DLG_LEAD", lead, context.leadModels, saveLead, saveCb, context.modalGenManager, context.props, context.props.i18nManager)}>
                                                <i className="fa fa-circle-info"></i>
                                                Consulter
                                            </button>
                                        )}
                                        {context.props.checkRights("LEAD_FLOWS", "L") && (
                                            <button className="quick-btn" onClick={() => context.showFlows(lead.idLead, lead)}>
                                                <i className="fa fa-clock-rotate-left"></i>
                                                Historique
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Files Section (Lead) */}
                            {context.props.checkRights("LEAD_FILES", "L") && (
                                <div className="files-section">
                                    <div className="card-header blue">
                                        <span className="card-title">
                                            <i className="fa fa-folder me-2"></i>
                                            Fichiers
                                        </span>
                                            <Button className="btn-icon rounded-circle m-0" size="sm" color="primary" style={{ width: "36px", height: "36px" }} onClick={() => { filesCustomRef.current.displayAddDialog(); }}>
                                            <i className="fa fa-plus me-1"></i>
                                        </Button>
                                    </div>
                                    <div className="card-body">
                                        <div className="files-group">
                                            <div className="files-group-title">Fichiers</div>
                                            <div className="files-list">
                                                <div className={`${filesAltObj.files?.length > 0 ? "" : "d-none"}`}>
                                                    <FilesCustom
                                                        ref={filesCustomRef}
                                                        className={"lead-files" + lead.idLead}
                                                        bindID={lead.idLead}
                                                        rightType={"LEAD_FILES"}
                                                        checkRights={context.props.checkRights}
                                                        fileTypeID="LEAD"
                                                        src={lead}
                                                        srcName={lead.title}
                                                        withFilters
                                                            noUploadBtn
                                                        shouldRender
                                                        altRendering
                                                            noRemove={"PDF"}
                                                            noContextMenu={false}
                                                            noChooseTypes
                                                        onlyDelete
                                                        defaultFileType={"DVS"}
                                                        {...filesAltObj}
                                                        altFilesUrl="../api/lead/GetLeadFiles"
                                                        altRemoveFilesUrl="../api/lead/RemoveLeadFile"
                                                        altUploadFilesUrl="../api/lead/UploadLeadFiles"
                                                        />
                                                </div>
                                                {filesAltObj.files?.length <= 0 && (
                                                    <div className="empty-files">
                                                        <i className="fa fa-inbox"></i>
                                                        <span>Aucun document</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        {filesRcpObj.files?.length > 0 && (
                                            <div className="files-group">
                                                <div className="files-group-title">Récapitulatif</div>
                                                <div className="files-list">
                                                    <FilesCustom
                                                        className={"lead-rcp-files" + lead.idLead}
                                                        bindID={lead.idLead}
                                                        rightType={"LEAD_FILES"}
                                                        checkRights={context.props.checkRights}
                                                        fileTypeID="LEAD"
                                                        src={lead}
                                                        srcName={lead.title}
                                                        withFilters
                                                        noUploadBtn
                                                        shouldRender
                                                        altRendering
                                                        noRemove
                                                        noContextMenu
                                                        onlyDelete
                                                        defaultFileType={"RCP"}
                                                        {...filesRcpObj}
                                                        altFilesUrl="../api/lead/GetLeadFiles"
                                                        altRemoveFilesUrl="../api/lead/RemoveLeadFile"
                                                        altUploadFilesUrl="../api/lead/UploadLeadFiles"
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default CardLead;